<template>
  <i-panel
    :visible="show"
    class="collect-session-modal"
    @hide-panel="hideCollectModal"
  >
    <template slot="header">
      <div class="d-flex w-100">
        <div class="w-100">
          <h4 class="i-text-heading-4 i-text-neutral-120 i-mb-2">Collect</h4>
          <h5 class="i-text-body-2 i-text-neutral-110 i-mb-0">
            {{ modalSubheading }}
          </h5>
        </div>
      </div>
    </template>
    <div
      id="collect-modal-content"
      class="d-flex flex-column justify-content-between"
    >
      <div v-if="!collectModalLoading">
        <div v-if="!collectSession">
          <div class="i-mb-10">
            <i-icon-toggle
              :options="toggleOptions"
              @toggle-selected="toggleEmail"
            ></i-icon-toggle>
          </div>

          <div v-if="collectSendEmail" class="i-mb-11">
            <label
              for="collect-email"
              class="i-text-subheading-2 i-text-neutral-120 i-mb-4"
              >Customer Email</label
            >
            <b-form-input
              id="collect-email"
              v-model="$v.collectEmail.$model"
              name="collect-email"
              placeholder="customer@example.com"
              class="i-form-input"
              :class="{
                error: collectSendEmail && hasAnyError($v.collectEmail),
              }"
              @input="delayTouch($v.collectEmail)"
            ></b-form-input>
            <div class="i-my-4 i-text-body-3 i-text-error-110">
              <p v-if="collectSendEmail && hasAnyError($v.collectEmail)">
                Must be a valid email address
              </p>
            </div>
          </div>

          <div
            v-if="customerInformationEnabled"
            id="information-requests"
            class="information-requests"
          >
            <div
              v-if="hasInformationRequests"
              :class="{
                'information-requests__options':
                  !collapseInformationRequestSection && hasDocumentRequests,
                'i-pb-11': collapseInformationRequestSection,
              }"
            >
              <div
                class="information-requests__heading d-flex align-items-center justify-content-between i-mt-11"
              >
                <div class="heading-tooltip d-flex flex-grow-1">
                  <h5
                    class="i-text-heading-5 i-text-neutral-120 i-m-0 i-mr-4"
                    @click="toggleCollapse('collapseInformationRequestSection')"
                  >
                    Information
                  </h5>
                  <button id="information-request-heading">
                    <i-icon
                      symbol="small-question-mark"
                      :size="12"
                      class="help-icon"
                    />
                  </button>

                  <b-tooltip
                    target="information-request-heading"
                    class="tooltip"
                    container="information-requests"
                  >
                    Specify what pieces of information you'd like your
                    <!--  -->
                    customer to provide
                  </b-tooltip>
                </div>

                <div class="actions d-flex">
                  <i-icon-button
                    class="remove"
                    symbol="medium-remove-1"
                    size="md"
                    type="transparent"
                    tooltip="Remove"
                    tooltip-container="information-requests"
                    tooltip-placement="top"
                    @button-click="removeAllSelectedInfoRequests"
                  ></i-icon-button>

                  <i-icon-button
                    symbol="medium-chevron-down"
                    size="md"
                    type="transparent"
                    tooltip="Collapse"
                    tooltip-container="information-requests"
                    tooltip-placement="top"
                    :class="{
                      collapsed: collapseInformationRequestSection,
                    }"
                    @button-click="
                      toggleCollapse('collapseInformationRequestSection')
                    "
                  ></i-icon-button>
                </div>
              </div>

              <div
                v-if="!collapseInformationRequestSection"
                class="information-requests__content"
              >
                <i-checkbox-group
                  v-model="selectedInformationOptions"
                  :options="informationOptions"
                  :sortable="true"
                />
              </div>
            </div>
            <div v-else>
              <request-card
                icon="medium-input-text-field"
                title="Customer Information"
                description="Send a request for information"
                new-label
                @add-request="toggleInformationRequest"
              ></request-card>
            </div>
          </div>

          <div id="document-requests" class="document-requests">
            <div v-if="hasDocumentRequests">
              <div
                class="document-requests__heading d-flex align-items-center justify-content-between i-my-9"
              >
                <div class="heading-tooltip d-flex flex-grow-1">
                  <h5
                    class="i-text-heading-5 i-text-neutral-120 i-m-0 i-mr-4"
                    @click="toggleCollapse('collapseDocumentRequestSection')"
                  >
                    Documents
                  </h5>
                  <button id="document-request-heading">
                    <i-icon
                      symbol="small-question-mark"
                      :size="12"
                      class="help-icon"
                    />
                  </button>

                  <b-tooltip
                    target="document-request-heading"
                    class="tooltip"
                    container="document-requests"
                  >
                    Specify what documents you'd like your customer to upload
                  </b-tooltip>
                </div>

                <div class="actions d-flex">
                  <i-icon-button
                    class="remove"
                    symbol="medium-remove-1"
                    size="md"
                    type="transparent"
                    tooltip="Remove"
                    tooltip-container="document-requests"
                    tooltip-placement="top"
                    @button-click="removeAllDocRequests"
                  ></i-icon-button>

                  <i-icon-button
                    symbol="medium-chevron-down"
                    size="md"
                    type="transparent"
                    tooltip="Collapse"
                    tooltip-container="document-requests"
                    tooltip-placement="top"
                    :class="{
                      collapsed: collapseDocumentRequestSection,
                    }"
                    @button-click="
                      toggleCollapse('collapseDocumentRequestSection')
                    "
                  ></i-icon-button>
                </div>
              </div>

              <div
                v-if="!collapseDocumentRequestSection"
                class="document-requests__content"
              >
                <div
                  v-for="(document, documentIndex) in $v.documents.$each.$iter"
                  :key="documentIndex"
                  draggable
                  class="d-flex flex-column i-mb-11 sortable"
                  @dragstart="onDragStart(documentIndex)"
                  @dragover="onDragOver($event, documentIndex)"
                  @drop="onDrop($event, documentIndex)"
                >
                  <div class="drag-handle">
                    <i-icon
                      symbol="small-drag-handle-vertical"
                      :size="16"
                      class="i-mr-2 i-mt-12"
                    />
                  </div>
                  <div
                    :id="`document-${documentIndex}`"
                    class="d-flex w-100 align-items-end"
                  >
                    <div class="flex-grow-1">
                      <label
                        :for="`document-label-${documentIndex}`"
                        class="i-text-subheading-2 i-text-neutral-120 i-mb-4"
                        >Document Title</label
                      >
                      <b-form-input
                        :id="`document-label-${documentIndex}`"
                        v-model.trim="document.label.$model"
                        :name="`document-label-${documentIndex}`"
                        placeholder="e.g. Bank Statement"
                        class="i-form-input"
                        :class="{ error: hasAnyError(document.label) }"
                        @input="delayTouch(document.label)"
                      ></b-form-input>
                    </div>

                    <div class="i-ml-4">
                      <label
                        :for="`document-max-number-${documentIndex}`"
                        class="i-text-subheading-2 i-text-neutral-120 i-mb-4"
                        >Max Docs</label
                      >
                      <b-form-input
                        :id="`document-max-number-${documentIndex}`"
                        v-model.number="document.max_documents.$model"
                        :name="`document-max-number-${documentIndex}`"
                        type="number"
                        class="i-form-input document-max-number"
                        placeholder="1"
                        min="1"
                        :class="{ error: hasAnyError(document.max_documents) }"
                      ></b-form-input>
                    </div>

                    <div
                      class="d-flex align-items-center justify-content-center"
                    >
                      <tags-input
                        id="upload"
                        class="i-ml-4 i-mb-1"
                        :value="document.$model.tags"
                        :allow-create="true"
                        :right="false"
                        boundary="window"
                        @input="onTagsChange($event, document.$model)"
                      >
                        <i-icon-button
                          symbol="medium-bookmark-tag"
                          size="xl"
                          type="light"
                          tooltip="Manage tags"
                          :tooltip-container="`document-${documentIndex}`"
                          tooltip-placement="top"
                        ></i-icon-button>
                      </tags-input>
                      <div>
                        <i-icon-button
                          class="i-ml-4 i-mb-1"
                          symbol="medium-delete-bin-1"
                          size="xl"
                          type="light"
                          tooltip="Delete"
                          :tooltip-container="`document-${documentIndex}`"
                          tooltip-placement="top"
                          @button-click="deleteDocRequest(documentIndex)"
                        ></i-icon-button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="document.$anyError"
                    class="i-my-4 i-text-body-3 i-text-error-110"
                  >
                    <p v-if="hasAnyError(document.label)">
                      The document title must have at least two characters
                    </p>
                    <p v-if="hasAnyError(document.max_documents)">
                      Please select between 1 and 70 documents
                    </p>
                  </div>
                  <tags
                    :id="`document-tags-${documentIndex}`"
                    :tags="document.$model.tags"
                  />

                  <div
                    v-if="liveCaptureEnabled"
                    class="d-flex align-items-center i-mt-6"
                  >
                    <i-switch
                      v-model="document.$model.live_capture_mode"
                      label="Live Image Capture"
                      size="sm"
                    />

                    <div class="i-ml-4 live-capture-tooltip">
                      <button :id="`live-capture-${documentIndex}-tooltip`">
                        <i-icon
                          symbol="small-question-mark"
                          :size="12"
                          class="help-icon"
                        />
                      </button>

                      <b-tooltip
                        :target="`live-capture-${documentIndex}-tooltip`"
                        class="tooltip"
                        :container="`document-${documentIndex}`"
                      >
                        <strong>Live image capture</strong><br />
                        By enabling live image capture, your customer will have
                        to take a photo of any documents requested from their
                        mobile device.
                      </b-tooltip>
                    </div>
                  </div>
                </div>

                <i-button
                  text="Add Document Request"
                  class="full-width i-mb-10"
                  type="neutral"
                  subtype="subtle"
                  size="lg"
                  @button-click="addDocument()"
                ></i-button>
              </div>
            </div>

            <div v-else>
              <request-card
                icon="medium-file"
                title="Customer Documents"
                description="Send a request for documents"
                @add-request="addDocument"
              ></request-card>
            </div>
          </div>
        </div>

        <div v-else>
          <div v-if="collectSendEmail">
            <i-alert-message
              type="success"
              heading="Collect Email Sent"
              :message="`An email has been sent to ${collectEmail}. You can also copy the link below and send it them yourself`"
            ></i-alert-message>
          </div>
          <p v-else>
            <i-alert-message
              type="success"
              heading="Collect Link Generated"
              message="Copy the link below and send it to your customer"
            ></i-alert-message>
          </p>
          <h2 class="i-text-subheading-2 i-text-neutral-120 i-mb-4 i-mt-10">
            Collect URL
          </h2>
          <i-copy
            element-id="session-url"
            :value="collectSession.url"
            container="collect-modal-content"
          ></i-copy>
        </div>
      </div>

      <div v-else>
        <i-spinner class="mx-auto i-my-16" size="xl"></i-spinner>
      </div>
    </div>

    <template slot="footer">
      <i-button
        v-if="!collectSession"
        text="Cancel"
        class="one-third-width i-mr-6"
        type="neutral"
        subtype="subtle"
        size="lg"
        @button-click="hideCollectModal()"
      ></i-button>
      <i-button
        :disabled="!isValidSession"
        :text="mainCTAText"
        :class="!collectSession ? 'two-thirds-width' : 'full-width'"
        size="lg"
        @button-click="createSession()"
      ></i-button>
    </template>
  </i-panel>
</template>

<script>
import { email, required, minLength, between } from 'vuelidate/lib/validators';
import { mapGetters, mapActions } from 'vuex';
import {
  iButton,
  iCopyInput,
  iIcon,
  iSpinner,
  iPanel,
  iIconButton,
  iAlertMessage,
  iIconToggle,
  iSwitch,
  iCheckboxGroup,
} from '@inscribe/signal';
import TagsInput from '@/V2/documents/TagsInput';
import Tags from '@/V2/documents/Tags';
import RequestCard from '@/V2/collect/RequestCard';
import * as request from '@/constants/informationRequestTypes';

const BLANK_DOCUMENT = {
  label: '',
  tags: [],
  max_documents: 1,
  live_capture_mode: false,
};

const BLANK_INFORMATION_OPTIONS = [
  request.TYPE_MAP.FULL_NAME,
  request.TYPE_MAP.EMAIL,
  request.TYPE_MAP.EIN,
  request.TYPE_MAP.SSN,
  request.TYPE_MAP.BUSINESS_ADDRESS,
  request.TYPE_MAP.PERSONAL_ADDRESS,
];

const touchMap = new WeakMap();

export default {
  components: {
    'tags-input': TagsInput,
    tags: Tags,
    'i-copy': iCopyInput,
    iButton,
    iIcon,
    iSpinner,
    iPanel,
    iIconButton,
    iAlertMessage,
    iIconToggle,
    iSwitch,
    iCheckboxGroup,
    RequestCard,
  },
  props: {
    selectedCustomer: {
      type: Object,
      default: () => {},
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collectEmail: '',
      collectSendEmail: false,
      collectSession: null,
      collectModalLoading: false,
      informationRequestsAdded: false,
      informationOptions: [...BLANK_INFORMATION_OPTIONS],
      collapseInformationRequestSection: false,
      collapseDocumentRequestSection: false,
      isDragging: false,
      documents: [{ ...BLANK_DOCUMENT }],
      text: {
        link: 'Generate Link',
        email: 'Send Email',
      },
      selectedInformationOptions: [],
    };
  },
  validations: {
    collectEmail: {
      email,
    },
    documents: {
      $each: {
        label: {
          required,
          minLength: minLength(2),
        },
        max_documents: {
          required,
          between: between(1, 70),
        },
      },
    },
  },
  computed: {
    ...mapGetters({
      liveCaptureEnabled: 'isCollectLiveCaptureEnabled',
      customerInformationEnabled: 'isCollectCustomerInformationFormEnabled',
    }),

    isValidSession() {
      const isEmailValid =
        !this.collectSendEmail ||
        (this.collectEmail && !this.hasAnyError(this.$v.collectEmail));

      const areDocumentsIfAddedValid =
        !this.hasDocumentRequests ||
        (this.hasDocumentRequests && !this.$v.documents.$invalid);

      const areInformationOptionsIfAddedValid =
        !this.hasInformationRequests ||
        (this.hasInformationRequests &&
          this.selectedInformationOptions.length > 0);

      const atLeastOneRequest =
        this.hasDocumentRequests || this.hasInformationRequests;

      return (
        isEmailValid &&
        atLeastOneRequest &&
        areDocumentsIfAddedValid &&
        areInformationOptionsIfAddedValid &&
        !this.collectModalLoading
      );
    },

    mainCTAText() {
      if (this.collectSession) {
        return 'Continue';
      } else {
        return this.collectSendEmail ? this.text.email : this.text.link;
      }
    },

    modalSubheading() {
      if (this.customerInformationEnabled) {
        return 'Request information and documents from your customer';
      } else {
        return 'Request documents from your customer';
      }
    },

    hasDocumentRequests() {
      return this.documents.length > 0;
    },

    hasInformationRequests() {
      return this.informationRequestsAdded;
    },

    toggleOptions() {
      return [
        {
          value: false,
          icon: 'medium-interface-link',
          text: this.text.link,
          active: !this.collectSendEmail,
        },
        {
          value: true,
          icon: 'medium-mail-envelope',
          text: this.text.email,
          active: this.collectSendEmail,
        },
      ];
    },
  },
  methods: {
    ...mapActions(['createCollectSession']),

    onTagsChange(newTags, document) {
      if (this.isDragging) {
        return;
      }
      document.tags = newTags.map((t) => ({ value: t }));
    },

    addDocument() {
      this.$nextTick(() => {
        this.$v.documents.$reset();
      });
      this.documents = [...this.documents, { ...BLANK_DOCUMENT }];
    },

    toggleInformationRequest() {
      this.informationRequestsAdded = !this.informationRequestsAdded;
    },

    deleteDocRequest(documentIndex) {
      this.documents.splice(documentIndex, 1);
    },

    removeAllDocRequests() {
      this.collapseDocumentRequestSection = false;
      this.documents = [];
    },

    removeAllSelectedInfoRequests() {
      this.informationRequestsAdded = false;
      this.collapseInformationRequestSection = false;
      this.selectedInformationOptions = [];
    },

    hideCollectModal() {
      this.$emit('hide');
      this.collectSession = null;
      this.collectEmail = '';
      this.documents = [{ ...BLANK_DOCUMENT }];
      this.collectSendEmail = false;
      this.collectModalLoading = false;
      this.collapseInformationRequestSection = false;
      this.collapseDocumentRequestSection = false;
      this.removeAllSelectedInfoRequests();
      this.$v.$reset();
    },

    createSession() {
      if (this.collectSession) {
        this.hideCollectModal();
      } else {
        this.collectModalLoading = true;

        this.createCollectSession({
          document_requests:
            this.documents.length > 0
              ? this.documents.map((document) => ({
                  ...document,
                  tags: document.tags.map((t) => ({ text: t.value })),
                }))
              : undefined,
          customer_information_requests:
            this.selectedInformationOptions.length > 0
              ? this.selectedInformationOptions
              : undefined,
          send_email: this.collectSendEmail,
          customer: {
            id: this.selectedCustomer.id,
            ...(this.collectSendEmail ? { email: this.collectEmail } : {}),
          },
        })
          .then(({ data }) => {
            this.collectSession = data;
            this.collectModalLoading = false;
          })
          .catch((error) => {
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'The collect session could not be created',
              duration: 5000,
              text: error.response.data.message,
            });
            this.collectModalLoading = false;
            this.hideCollectModal();
          });
      }
    },

    hasAnyError(validation) {
      return validation.$dirty && validation.$error && validation.$invalid;
    },

    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },

    toggleEmail(value) {
      this.collectSendEmail = value;
    },

    toggleCollapse(section) {
      this[section] = !this[section];
    },

    onDragStart(index) {
      this.isDragging = true;
      event.dataTransfer.setData('text/plain', index.toString());
    },
    onDragOver(event, index) {
      event.preventDefault();
    },
    onDrop(event, index) {
      const startIndex = parseInt(event.dataTransfer.getData('text/plain'), 10);
      const droppedDocument = this.documents[startIndex];
      this.documents.splice(startIndex, 1);
      this.documents.splice(index, 0, droppedDocument);
      this.isDragging = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.collect-session-modal {
  .full-width {
    width: 100%;
  }

  .one-third-width {
    width: 33%;
  }

  .two-thirds-width {
    width: 66%;
  }

  .heading-tooltip,
  .live-capture-tooltip {
    button {
      cursor: pointer;
      border: 0;
      padding: 0;
      background: transparent;
      .help-icon {
        color: $light-neutral-80 !important;
      }
      &:focus,
      &:hover {
        .help-icon {
          color: $light-neutral-100 !important;
        }
      }
    }
  }
  .document-max-number {
    width: 120px;
  }

  .information-requests {
    &__options {
      border-bottom: 1px solid $light-neutral-60;
    }

    ::v-deep .i-radio-checkbox {
      padding-top: 20px !important;
    }
  }
  .information-requests,
  .document-requests {
    &__heading {
      .actions {
        .remove {
          visibility: hidden;
        }
      }

      &:hover {
        .actions {
          .remove {
            visibility: visible;
          }
        }
      }
    }

    .sortable {
      position: relative;
      margin-left: -38px;
      padding-left: 38px;
      .drag-handle {
        display: none;
        position: absolute;
        justify-content: flex-end;
        width: 40px;
        height: 100%;
        left: -2px;
        cursor: grab;
        .i-icon {
          color: $light-neutral-60 !important;
        }
      }

      &:hover {
        .drag-handle {
          display: flex;
          &:hover {
            .i-icon {
              color: $light-neutral-80 !important;
            }
          }
        }
      }
    }
  }
}

::v-deep .document-tag {
  margin-right: 4px;
  margin-top: 8px;
}
</style>
